import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { normalizeAutoTaggingPlanNameQuery } from "../../../services/autoTaggingService";
import {
  fetchOtoPlans,
  OTO_PLANS_DEFAULT_PAGE_SIZE,
  type OtoPlan,
} from "../../../services/objectToObjectPlanService";
import {
  AI_PLAN_ADD_OTO_PLAN_PATH,
  buildEditOtoPlanPath,
} from "../constants";
import ObjectToObjectPlanFilterBar, {
  EMPTY_OTO_PLAN_FILTERS,
  type ObjectToObjectPlanAppliedFilters,
} from "./ObjectToObjectPlanFilterBar";
import ObjectToObjectPlanTable from "./ObjectToObjectPlanTable";

export default function ObjectToObjectPlanTab() {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [searchText, setSearchText] = useState("");
  const [appliedFilters, setAppliedFilters] =
    useState<ObjectToObjectPlanAppliedFilters>(EMPTY_OTO_PLAN_FILTERS);
  const [rows, setRows] = useState<OtoPlan[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: OTO_PLANS_DEFAULT_PAGE_SIZE,
  });
  const requestIdRef = useRef(0);

  const apiKeyword = normalizeAutoTaggingPlanNameQuery(searchText);
  const apiName =
    appliedFilters.assessmentNames.length === 1
      ? normalizeAutoTaggingPlanNameQuery(
          appliedFilters.assessmentNames[0] ?? "",
        )
      : "";
  const apiObjectB =
    appliedFilters.impactItemKeys.length === 1
      ? appliedFilters.impactItemKeys[0]
      : "";

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [appliedFilters, searchText]);

  const loadRows = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    fetchOtoPlans({
      objectB: apiObjectB,
      keyword: apiKeyword,
      name: apiName,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        setRows(result.items);
        setTotal(result.total);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setRows([]);
        setTotal(0);
        showMessage("Failed to load Object to Object.", "error");
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
        setHasLoaded(true);
      });
  }, [
    apiKeyword,
    apiName,
    apiObjectB,
    paginationModel.page,
    paginationModel.pageSize,
    showMessage,
  ]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleEdit = useCallback(
    (row: OtoPlan) => {
      navigate(buildEditOtoPlanPath(row.id), { state: { plan: row } });
    },
    [navigate],
  );

  const showInitialLoading = !hasLoaded && loading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
        mb: "16px",
      }}
    >
      <ObjectToObjectPlanFilterBar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        appliedFilters={appliedFilters}
        onAppliedFiltersChange={setAppliedFilters}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <ObjectToObjectPlanTable
          rows={rows}
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onAddNew={() => navigate(AI_PLAN_ADD_OTO_PLAN_PATH)}
          onEdit={handleEdit}
          onPlanDeleted={loadRows}
          onPlanUpdated={loadRows}
        />
      )}
    </Box>
  );
}
